/**
 * Dashboard Service
 * Handles all dashboard-related API calls
 */

import apiClient from '../client';
import { API_ENDPOINTS } from '../config';
import {
  isDisplayableAppMetricVersion,
  isDisplayableRoadmapAppVersion,
} from '../../utils/appVersionDisplay';
import {
  DashboardStats,
  VersionStats,
  RetentionData,
  RetentionResponse,
  MetricsData,
  AppMetricsResponse,
  AppIdMetricsDetail,
  AppIdMetricsOverview,
  PaginatedResponse,
  DRFPaginatedResponse,
  User,
  UserDetail,
  GameSession,
  GameSessionDetail,
  ChatThread,
  ChatSessionDetail,
  ChatMessageResponse,
  AppSession,
  TestSession,
  TestSessionDetail,
  TableCountsResponse,
  MetricsByCountryResponse,
  MetricsByCountryItem,
  MetricsByAgeRangeItem,
  MetricsByAgeRangeResponse,
  GameTestRankItem,
  SessionOverviewsResponse,
  ProfileTreeResponse,
  RoadmapListResponse,
  RoadmapListParams,
  RoadmapItem,
  RoadmapDetailResponse,
  RoadmapDetailItem,
} from '../types';

class DashboardService {
  /**
   * Get dashboard overview statistics
   */
  async getOverview(): Promise<DashboardStats> {
    const response = await apiClient.get<DashboardStats>(
      API_ENDPOINTS.DASHBOARD.OVERVIEW
    );
    return response.data;
  }

  /**
   * Get general statistics
   */
  async getStats(params?: {
    dateFrom?: string;
    dateTo?: string;
    country?: string;
  }): Promise<{
    appSessions: number;
    gameSessions: number;
    chatThreads: number;
    testSessions: number;
    users: number;
  }> {
    const response = await apiClient.get<{
      appSessions: number;
      gameSessions: number;
      chatThreads: number;
      testSessions: number;
      users: number;
    }>(API_ENDPOINTS.DASHBOARD.STATS, { params });
    return response.data;
  }

  /**
   * Get version statistics
   */
  async getVersionStats(type: 'sessions' | 'users' | 'games' | 'tests' | 'chats'): Promise<{
    stats: VersionStats[];
    totals: {
      last24h: number;
      lastWeek: number;
      total: number;
    };
  }> {
    const response = await apiClient.get<{
      stats: VersionStats[];
      totals: {
        last24h: number;
        lastWeek: number;
        total: number;
      };
    }>(`${API_ENDPOINTS.DASHBOARD.STATS}/version`, {
      params: { type },
    });
    return response.data;
  }

  /**
   * Get retention data
   */
  async getRetentionData(params?: {
    country_code?: string | null;
    country?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<RetentionData[]> {
    // Build query params - only include country_code if it's not null
    const queryParams: Record<string, any> = {};
    if (params?.country_code !== null && params?.country_code !== undefined) {
      queryParams.country_code = params.country_code;
    }
    if (params?.dateFrom) {
      queryParams.dateFrom = params.dateFrom;
    }
    if (params?.dateTo) {
      queryParams.dateTo = params.dateTo;
    }
    
    const response = await apiClient.get<RetentionResponse>(
      API_ENDPOINTS.DASHBOARD.RETENTION,
      { params: queryParams }
    );
    
    // API returns { percentage: [...] }, extract the array
    if (Array.isArray(response)) {
      return response;
    }
    
    if (response && typeof response === 'object' && 'percentage' in response) {
      return (response as any).percentage || [];
    }
    
    // Fallback: check if response.data exists
    if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as any).data;
      if (Array.isArray(data)) {
        return data;
      }
    }
    
    return [];
  }

  /**
   * Get app metrics data
   */
  async getMetrics(params?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<MetricsData[]> {
    const response = await apiClient.get<MetricsData[]>(
      API_ENDPOINTS.DASHBOARD.METRICS,
      { params }
    );
    return response.data;
  }

  /**
   * Get chart data for dashboard
   */
  async getChartData(
    chartType: 'bar' | 'line',
    dataType: 'sessions' | 'games' | 'users'
  ): Promise<Array<{ name: string; [key: string]: string | number }>> {
    const response = await apiClient.get<Array<{ name: string; [key: string]: string | number }>>(
      `${API_ENDPOINTS.DASHBOARD.STATS}/chart`,
      {
        params: {
          chartType,
          dataType,
        },
      }
    );
    return response.data;
  }

  /**
   * Get top games
   */
  async getTopGames(params?: {
    limit?: number;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Array<{
    gameId: string;
    gameName: string;
    playCount: number;
    percentage: number;
  }>> {
    const response = await apiClient.get<Array<{
      gameId: string;
      gameName: string;
      playCount: number;
      percentage: number;
    }>>(`${API_ENDPOINTS.DASHBOARD.STATS}/top-games`, { params });
    return response.data;
  }

  /**
   * Refresh dashboard data
   */
  async refreshData(): Promise<void> {
    await apiClient.post(`${API_ENDPOINTS.DASHBOARD.STATS}/refresh`);
  }

  /**
   * Get user details with pagination
   */
  async getUserDetails(params?: {
    page?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<UserDetail>> {
    // Only send page parameter, remove limit
    const queryParams: Record<string, any> = {};
    if (params?.page) {
      queryParams.page = params.page;
    }
    if (params?.search) {
      queryParams.search = params.search;
    }
    if (params?.sortBy) {
      queryParams.sortBy = params.sortBy;
    }
    if (params?.sortOrder) {
      queryParams.sortOrder = params.sortOrder;
    }
    
    // API returns Django REST Framework pagination format directly
    const apiResponse = await apiClient.get<DRFPaginatedResponse<UserDetail>>(
      API_ENDPOINTS.DASHBOARD.USER_DETAILS,
      { params: queryParams }
    );
    
    // API returns DRF format directly, check if wrapped in ApiResponse or direct
    const drfResponse = (apiResponse as any).data || apiResponse as any;
    const currentPage = params?.page || 1;
    const itemsPerPage = drfResponse.results?.length || 10;
    const totalPages = Math.ceil((drfResponse.count || 0) / itemsPerPage);
    
    return {
      data: drfResponse.results || [],
      pagination: {
        total: drfResponse.count || 0,
        page: currentPage,
        limit: itemsPerPage,
        totalPages: totalPages || 1,
      },
    };
  }

  /**
   * Get game session details with pagination
   */
  async getGameSessionDetails(params?: {
    page?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<GameSessionDetail>> {
    // Only send page parameter, remove limit
    const queryParams: Record<string, any> = {};
    if (params?.page) {
      queryParams.page = params.page;
    }
    if (params?.search) {
      queryParams.search = params.search;
    }
    if (params?.sortBy) {
      queryParams.sortBy = params.sortBy;
    }
    if (params?.sortOrder) {
      queryParams.sortOrder = params.sortOrder;
    }
    
    // API returns Django REST Framework pagination format directly
    const apiResponse = await apiClient.get<DRFPaginatedResponse<GameSessionDetail>>(
      API_ENDPOINTS.DASHBOARD.GAME_SESSION_DETAILS,
      { params: queryParams }
    );
    
    // API returns DRF format directly, check if wrapped in ApiResponse or direct
    const drfResponse = (apiResponse as any).data || apiResponse as any;
    const currentPage = params?.page || 1;
    const itemsPerPage = drfResponse.results?.length || 10;
    const totalPages = Math.ceil((drfResponse.count || 0) / itemsPerPage);
    
    return {
      data: drfResponse.results || [],
      pagination: {
        total: drfResponse.count || 0,
        page: currentPage,
        limit: itemsPerPage,
        totalPages: totalPages || 1,
      },
    };
  }

  /**
   * Get chat session details with pagination
   */
  async getChatSessionDetails(params?: {
    page?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<ChatSessionDetail>> {
    // Only send page parameter, remove limit
    const queryParams: Record<string, any> = {};
    if (params?.page) {
      queryParams.page = params.page;
    }
    if (params?.search) {
      queryParams.search = params.search;
    }
    if (params?.sortBy) {
      queryParams.sortBy = params.sortBy;
    }
    if (params?.sortOrder) {
      queryParams.sortOrder = params.sortOrder;
    }
    
    // API returns Django REST Framework pagination format directly
    const apiResponse = await apiClient.get<DRFPaginatedResponse<ChatSessionDetail>>(
      API_ENDPOINTS.DASHBOARD.CHAT_SESSION_DETAILS,
      { params: queryParams }
    );
    
    // API returns DRF format directly, check if wrapped in ApiResponse or direct
    const drfResponse = (apiResponse as any).data || apiResponse as any;
    const currentPage = params?.page || 1;
    const itemsPerPage = drfResponse.results?.length || 10;
    const totalPages = Math.ceil((drfResponse.count || 0) / itemsPerPage);
    
    return {
      data: drfResponse.results || [],
      pagination: {
        total: drfResponse.count || 0,
        page: currentPage,
        limit: itemsPerPage,
        totalPages: totalPages || 1,
      },
    };
  }

  /**
   * Get chat sessions by player ID
   */
  async getChatSessionsByPlayerId(playerId: string): Promise<ChatSessionDetail[]> {
    const response = await apiClient.get<ChatSessionDetail[]>(
      `${API_ENDPOINTS.DASHBOARD.CHAT_SESSION_DETAILS}${playerId}/`
    );
    return Array.isArray(response) ? response : (response as any).data || [];
  }

  /**
   * Get chat messages for a specific thread
   */
  async getChatMessages(threadId: string): Promise<ChatMessageResponse[]> {
    const response = await apiClient.get<ChatMessageResponse[]>(
      API_ENDPOINTS.DASHBOARD.CHAT_SESSION_READ,
      { params: { thread_id: threadId } }
    );
    
    // API returns array directly (not wrapped in ApiResponse)
    // Check if response is already an array
    if (Array.isArray(response)) {
      return response;
    }
    
    // If response has data property and it's an array
    if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as any).data;
      if (Array.isArray(data)) {
        return data;
      }
    }
    
    // Fallback: return empty array if structure is unexpected
    console.warn('Unexpected API response structure for chat messages:', response);
    return [];
  }

  /**
   * Get app metrics data
   */
  async getAppMetrics(params?: {
    country_code?: string | null;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<AppMetricsResponse[]> {
    // Build query params - only include country_code if it's not null
    const queryParams: Record<string, any> = {};
    if (params?.country_code !== null && params?.country_code !== undefined) {
      queryParams.country_code = params.country_code;
    }
    if (params?.dateFrom) {
      queryParams.dateFrom = params.dateFrom;
    }
    if (params?.dateTo) {
      queryParams.dateTo = params.dateTo;
    }
    
    const response = await apiClient.get<AppMetricsResponse[]>(
      API_ENDPOINTS.DASHBOARD.APP_METRICS,
      { params: queryParams }
    );
    
    // API returns array directly
    if (Array.isArray(response)) {
      return response;
    }
    
    // If response has data property
    if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as any).data;
      if (Array.isArray(data)) {
        return data;
      }
    }
    
    return [];
  }

  /**
   * Get app session details with pagination
   */
  async getAppSessionDetails(params?: {
    page?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<AppSession>> {
    // Only send page parameter, remove limit
    const queryParams: Record<string, any> = {};
    if (params?.page) {
      queryParams.page = params.page;
    }
    if (params?.search) {
      queryParams.search = params.search;
    }
    if (params?.sortBy) {
      queryParams.sortBy = params.sortBy;
    }
    if (params?.sortOrder) {
      queryParams.sortOrder = params.sortOrder;
    }
    
    // API returns Django REST Framework pagination format directly
    // apiClient.get returns ApiResponse<T>, but this API returns DRF format directly
    const apiResponse = await apiClient.get<DRFPaginatedResponse<AppSession>>(
      API_ENDPOINTS.DASHBOARD.APP_SESSION_DETAILS,
      { params: queryParams }
    );
    
    // API returns DRF format directly, check if wrapped in ApiResponse or direct
    const drfResponse = (apiResponse as any).data || apiResponse as any;
    const currentPage = params?.page || 1;
    const rawResults: AppSession[] = drfResponse.results || [];
    const filteredResults = rawResults.filter((row) => isDisplayableAppMetricVersion(row.app_id));
    const itemsPerPage = rawResults.length || 10;
    const totalPages = Math.ceil((drfResponse.count || 0) / itemsPerPage);

    return {
      data: filteredResults,
      pagination: {
        total: drfResponse.count || 0,
        page: currentPage,
        limit: itemsPerPage,
        totalPages: totalPages || 1,
      },
    };
  }

  /**
   * Get test session details with pagination
   */
  async getTestSessionDetails(params?: {
    page?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<TestSessionDetail>> {
    // Only send page parameter, remove limit
    const queryParams: Record<string, any> = {};
    if (params?.page) {
      queryParams.page = params.page;
    }
    if (params?.search) {
      queryParams.search = params.search;
    }
    if (params?.sortBy) {
      queryParams.sortBy = params.sortBy;
    }
    if (params?.sortOrder) {
      queryParams.sortOrder = params.sortOrder;
    }
    
    // API returns Django REST Framework pagination format directly
    const apiResponse = await apiClient.get<DRFPaginatedResponse<TestSessionDetail>>(
      API_ENDPOINTS.DASHBOARD.TEST_SESSION_DETAILS,
      { params: queryParams }
    );
    
    // API returns DRF format directly, check if wrapped in ApiResponse or direct
    const drfResponse = (apiResponse as any).data || apiResponse as any;
    const currentPage = params?.page || 1;
    const itemsPerPage = drfResponse.results?.length || 10;
    const totalPages = Math.ceil((drfResponse.count || 0) / itemsPerPage);
    
    return {
      data: drfResponse.results || [],
      pagination: {
        total: drfResponse.count || 0,
        page: currentPage,
        limit: itemsPerPage,
        totalPages: totalPages || 1,
      },
    };
  }

  /**
   * Get table counts for dashboard
   */
  async getTableCounts(): Promise<TableCountsResponse> {
    const response = await apiClient.get<TableCountsResponse>(
      API_ENDPOINTS.DASHBOARD.TABLE_COUNTS
    );
    return response as any;
  }

  /**
   * Get app ID metrics details
   */
  async getAppIdMetricsDetails(params: {
    metrics_type: 'app_session' | 'game_session' | 'test_session' | 'chat_session' | 'users' | 'advertisements';
  }): Promise<AppIdMetricsDetail[]> {
    const response = await apiClient.get<AppIdMetricsDetail[]>(
      API_ENDPOINTS.DASHBOARD.APP_ID_METRICS_DETAILS,
      { params }
    );
    
    let rawData: any[] = [];
    
    // API returns array directly (not wrapped in ApiResponse)
    if (Array.isArray(response)) {
      rawData = response;
    } 
    // Fallback: check if response.data exists
    else if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as any).data;
      if (Array.isArray(data)) {
        rawData = data;
      }
    }
    
    // Normalize: API inconsistency - some metrics use daily_sessions_count, others use daily_session_count
    // Also handle app_version vs app_id field name difference
    return rawData
      .map((item: any) => ({
        app_id: item.app_id ?? item.app_version ?? '',
        session_date: item.session_date,
        // Use whichever field exists (prefer daily_sessions_count if both exist)
        daily_sessions_count: item.daily_sessions_count ?? item.daily_session_count ?? 0,
      }))
      .filter((row) => isDisplayableAppMetricVersion(row.app_id));
  }

  /**
   * Get app ID metrics overview
   */
  async getAppIdMetricsOverview(params?: {
    metrics_type?:
      | 'app_session'
      | 'game_session'
      | 'test_session'
      | 'chat_session'
      | 'users'
      | 'advertisements';
  }): Promise<AppIdMetricsOverview[]> {
    const response = await apiClient.get<AppIdMetricsOverview[]>(
      API_ENDPOINTS.DASHBOARD.APP_ID_METRICS_OVERVIEW,
      { params }
    );

    const normalizeOverview = (rows: AppIdMetricsOverview[]): AppIdMetricsOverview[] =>
      rows
        .map((item: any) => ({
          ...item,
          app_id: item.app_id ?? item.app_version ?? '',
        }))
        .filter((item) => isDisplayableAppMetricVersion(item.app_id));

    // API returns array directly
    if (Array.isArray(response)) {
      return normalizeOverview(response);
    }

    // Fallback: check if response.data exists
    if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as any).data;
      if (Array.isArray(data)) {
        return normalizeOverview(data);
      }
    }

    return [];
  }

  /**
   * Get metrics by country
   */
  async getMetricsByCountry(params?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<MetricsByCountryItem[]> {
    const response = await apiClient.get<MetricsByCountryResponse>(
      API_ENDPOINTS.DASHBOARD.METRICS_BY_COUNTRY,
      { params }
    );
    
    // API returns { app_session: [...], game_session: [...], test_session: [...] }
    // Combine all three types into a single array
    const allMetrics: MetricsByCountryItem[] = [];
    
    // Extract data from ApiResponse wrapper if needed
    const responseData = (response as any).data || response;
    
    if (responseData && typeof responseData === 'object') {
      const metricsResponse = responseData as MetricsByCountryResponse;
      
      // Add metric_type to each item for identification
      if (metricsResponse.app_session) {
        allMetrics.push(...metricsResponse.app_session.map(item => ({ ...item, metric_type: 'app_session' as const })));
      }
      if (metricsResponse.game_session) {
        allMetrics.push(...metricsResponse.game_session.map(item => ({ ...item, metric_type: 'game_session' as const })));
      }
      if (metricsResponse.test_session) {
        allMetrics.push(...metricsResponse.test_session.map(item => ({ ...item, metric_type: 'test_session' as const })));
      }
    }
    
    if (allMetrics.length > 0) {
      return allMetrics.filter((row) => isDisplayableAppMetricVersion(row.app_id));
    }

    return [];
  }

  /**
   * Get metrics by age range
   */
  async getMetricsByAgeRange(params?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<MetricsByAgeRangeItem[]> {
    const response = await apiClient.get<MetricsByAgeRangeResponse>(
      API_ENDPOINTS.DASHBOARD.METRICS_BY_AGE_RANGE,
      { params }
    );
    
    // API returns { app_session: [...], game_session: [...], test_session: [...] }
    // Combine all three types into a single array
    const allMetrics: MetricsByAgeRangeItem[] = [];
    
    // Extract data from ApiResponse wrapper if needed
    const responseData = (response as any).data || response;
    
    if (responseData && typeof responseData === 'object') {
      const metricsResponse = responseData as MetricsByAgeRangeResponse;
      
      // Add metric_type to each item for identification
      if (metricsResponse.app_session) {
        allMetrics.push(...metricsResponse.app_session.map(item => ({ ...item, metric_type: 'app_session' as const })));
      }
      if (metricsResponse.game_session) {
        allMetrics.push(...metricsResponse.game_session.map(item => ({ ...item, metric_type: 'game_session' as const })));
      }
      if (metricsResponse.test_session) {
        allMetrics.push(...metricsResponse.test_session.map(item => ({ ...item, metric_type: 'test_session' as const })));
      }
    }
    
    if (allMetrics.length > 0) {
      return allMetrics.filter((row) => isDisplayableAppMetricVersion(row.app_id));
    }

    return [];
  }

  /**
   * Get game/test rank data
   */
  async getGameTestRank(params?: {
    type?: 'games' | 'tests';
    dateFrom?: string;
    dateTo?: string;
  }): Promise<GameTestRankItem[]> {
    const queryParams: Record<string, any> = {};
    if (params?.type) {
      queryParams.type = params.type;
    }
    if (params?.dateFrom) {
      queryParams.dateFrom = params.dateFrom;
    }
    if (params?.dateTo) {
      queryParams.dateTo = params.dateTo;
    }

    const response = await apiClient.get<GameTestRankItem[]>(
      API_ENDPOINTS.DASHBOARD.GAME_TEST_RANK,
      { params: queryParams }
    );
    
    // Extract data from ApiResponse wrapper if needed
    const responseData = (response as any).data || response;
    
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    return [];
  }

  /**
   * Get session overviews (game and test sessions)
   */
  async getSessionOverviews(params?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<SessionOverviewsResponse> {
    const response = await apiClient.get<SessionOverviewsResponse>(
      API_ENDPOINTS.DASHBOARD.SESSION_OVERVIEWS,
      { params }
    );
    
    const filterSessionOverviewRows = <T extends { app_id: string }>(rows: T[]): T[] =>
      rows.filter((row) => isDisplayableAppMetricVersion(row.app_id));

    // API returns { game_session: [...], test_session: [...] }
    if (response && typeof response === 'object') {
      return {
        game_session: filterSessionOverviewRows((response as any).game_session || []),
        test_session: filterSessionOverviewRows((response as any).test_session || []),
      };
    }

    // Fallback: check if response.data exists
    if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as any).data;
      if (data && typeof data === 'object') {
        return {
          game_session: filterSessionOverviewRows(data.game_session || []),
          test_session: filterSessionOverviewRows(data.test_session || []),
        };
      }
    }
    
    return {
      game_session: [],
      test_session: [],
    };
  }

  /**
   * Get profile tree for a player
   */
  async getProfileTree(params: {
    player_id: string;
    type: 'tests' | 'profile';
  }): Promise<ProfileTreeResponse> {
    const response = await apiClient.get<ProfileTreeResponse>(
      API_ENDPOINTS.DASHBOARD.PROFILE_TREE,
      { params }
    );
    
    // API returns object directly
    if (response && typeof response === 'object') {
      // Check if it's wrapped in ApiResponse
      if ('data' in response && typeof (response as any).data === 'object') {
        return (response as any).data as ProfileTreeResponse;
      }
      // Otherwise return as is
      return response as unknown as ProfileTreeResponse;
    }
    
    return {};
  }

  /**
   * Get roadmap list
   */
  async getRoadmapList(params?: RoadmapListParams): Promise<RoadmapListResponse> {
    const queryParams: Record<string, string | number | boolean> = {};
    if (params?.page != null) queryParams.page = params.page;
    if (params?.platform) queryParams.platform = params.platform;
    if (params?.completed_from_roadmap != null) {
      queryParams.completed_from_roadmap = params.completed_from_roadmap;
    }
    if (params?.min_origin != null) queryParams.min_origin = params.min_origin;
    if (params?.from_date) queryParams.from_date = params.from_date;
    if (params?.upto_date) queryParams.upto_date = params.upto_date;
    if (params?.last_activity_n_days_ago != null) {
      queryParams.last_activity_n_days_ago = params.last_activity_n_days_ago;
    }
    if (params?.player_id) queryParams.player_id = params.player_id;
    if (params?.user_type) queryParams.user_type = params.user_type;

    const response = await apiClient.get<RoadmapListResponse>(
      API_ENDPOINTS.DASHBOARD.ROADMAP,
      { params: queryParams }
    );
    
    // API returns DRF format directly
    const drfResponse = (response as any).data || response as any;
    const results = (drfResponse.results || []).filter((row: RoadmapItem) =>
      isDisplayableRoadmapAppVersion(row.app_version)
    );

    return {
      count: drfResponse.count || 0,
      next: drfResponse.next || null,
      previous: drfResponse.previous || null,
      results,
    };
  }

  /**
   * Get roadmap details for a specific player
   */
  async getRoadmapDetail(playerId: string): Promise<RoadmapDetailResponse> {
    const response = await apiClient.get<RoadmapDetailResponse>(
      API_ENDPOINTS.USERS.PLAYER_ROADMAP(playerId)
    );
    
    // Handle different response formats:
    // 1. ApiResponse wrapped: { success: true, data: { data: [...], meta: {} } }
    // 2. Direct response: { data: [...], meta: {} }
    // 3. Direct array: [...]
    
    let responseData: any = response;
    
    // Check if wrapped in ApiResponse format
    if (response && typeof response === 'object' && 'data' in response) {
      responseData = (response as any).data;
    }
    
    // Now responseData should be { data: [...], meta: {} } or [...]
    if (responseData && typeof responseData === 'object') {
      // If it has 'data' array property, it's the correct format
      if ('data' in responseData && Array.isArray(responseData.data)) {
        return {
          data: responseData.data.filter((row: RoadmapDetailItem) =>
            isDisplayableRoadmapAppVersion(row.app_version)
          ),
          meta: responseData.meta || {},
        };
      }
      // If responseData itself is an array
      if (Array.isArray(responseData)) {
        return {
          data: (responseData as RoadmapDetailItem[]).filter((row) =>
            isDisplayableRoadmapAppVersion(row.app_version)
          ),
          meta: {},
        };
      }
    }
    
    // Log for debugging
    console.log('Roadmap detail - Full response:', response);
    console.log('Roadmap detail - Extracted data:', responseData);
    
    return {
      data: [],
      meta: {},
    };
  }
}

// Export singleton instance
export const dashboardService = new DashboardService();
export default dashboardService;

